import {
  DeviceCategoryEnum,
  DEVICES_TYPES_SETTINGS,
  IfcClassesEnum,
  getDeeplyNestedValue,
  getMcbimItemClassificationInput,
  isChillerDevice,
  isFireSuppression,
  isKvadradiskInfeldArmatur,
  isReturnAirterminal,
  isSensor,
  isSupplyAirTerminal,
  project,
  scalarProd,
  sortMcBimItemsFurnituresFirst,
  transform,
  vectorAdd,
  vectorSubtract,
} from "@multiconsult-group/multiplacer-core";
import {
  IDeviceInputMl,
  IDirectionDepricated,
  IGrid,
  IMLResult,
  ISpaceTrainingDataInput,
} from "./types/types";
import { IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { TreeNodeData } from "@mantine/core";
import { sortArrayByStrings } from "./globalUtils";
import {
  IBoundingBox,
  IMcBimItem,
  IPolygon,
} from "@backend/src/shared/schemas/schemas";
import { GetMcbimFileFloorVersionsApiResponse, GetMcbimSpacesApiResponse } from "../openapi";
import { IDeviceCategoryEnum, IDirection } from "@multiconsult-group/multiplacer-core";


export const createNormalVectorPath = (
  coordinate: number[],
  direction: number[] | undefined
): IPixPath | null => {
  if (!coordinate || !direction) {
    return null;
  }

  const startPoint = coordinate;
  const endPoint = [coordinate[0] + direction[0], coordinate[1] + direction[1]];

  return {
    id: generateRandomString(5),
    points: [startPoint, endPoint],
    strokeColor: "#f60b0b",
    strokeWidth: "1",
  };
};

export interface FormatMcBimItemsToPixPolygonsArgs {
  mcBimItems: IMcBimItem[];
  showDuctSegment?: boolean;
  compCodePath: string;
}

export const formatMcBimItemsToPixPolygons = ({
  mcBimItems,
  compCodePath,
  showDuctSegment = false,
}: FormatMcBimItemsToPixPolygonsArgs): IPixPolygon[] => {
  return sortMcBimItemsFurnituresFirst(mcBimItems)?.reduce((acc, mcBimItem) => {
    if (mcBimItem.centerPoint != null && mcBimItem.bbox != null) {
      if (mcBimItem.instanceClass === "IfcDuctSegment" && !showDuctSegment) {
        return acc;
      }

      const typeClass = mcBimItem.type?.typeClass;

      if (typeClass == null) {
        return acc;
      }

      const compCode = getDeeplyNestedValue(mcBimItem, compCodePath) ?? "";
      const genericItem: IGenericBimItemPolygon = {
        bbox: mcBimItem.bbox,
        text: compCode,
        tooltip: `${compCode},${mcBimItem.name}, ${mcBimItem.type?.name}, ${mcBimItem.instanceClass}`,
        deviceType: DeviceCategoryEnum.DEFAULT_DEVICE,
      };
      if (genericItem.bbox == null) {
        return acc;
      }

      const classificationInput = getMcbimItemClassificationInput({
        mcbimItem: mcBimItem,
        compCodePath,
      });

      if (isChillerDevice(classificationInput)) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.CHILLER,
          })
        );
        return acc;
      }

      if (mcBimItem.instanceClass === IfcClassesEnum.IFCFURNITURE) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.FURNITURE,
          })
        );
        return acc;
      }
      if (isSupplyAirTerminal(classificationInput)) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.SUPPLY_AIR_DEVICE,
          })
        );
        return acc;
      }
      if (isReturnAirterminal(classificationInput)) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.RETURN_AIR_DEVICE,
          })
        );
        return acc;
      }
      if (isKvadradiskInfeldArmatur(classificationInput)) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.LIGHT_DEVICE,
          })
        );
        return acc;
      }
      if (isFireSuppression(classificationInput)) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE,
          })
        );
        return acc;
      }

      if (isSensor(classificationInput)) {
        acc.push(
          formatPixPolygonFromGenericBimItem({
            ...genericItem,
            deviceType: DeviceCategoryEnum.SENSOR_DEVICE,
          })
        );
        return acc;
      }

      acc.push(
        formatPixPolygonFromGenericBimItem({
          ...genericItem,
          deviceType: DeviceCategoryEnum.DEFAULT_DEVICE,
        })
      );
      return acc;
    }
    return acc;
  }, [] as IPixPolygon[]);
};

export const formatMcBimItemsToDeviceInputML = (
  mcBimItems: IMcBimItem[],
  deviceType: DeviceCategoryEnum,
  compCodePath: string
): IDeviceInputMl[] => {
  return mcBimItems?.reduce((acc, mcBimItem) => {
    if (mcBimItem.centerPoint != null && mcBimItem.bbox != null) {
      const typeClass = mcBimItem.type?.typeClass;
      let device: IDeviceInputMl = {
        name: mcBimItem.name,
        centerPoint: mcBimItem.centerPoint,
        bbox: mcBimItem.bbox,
      };
      if (typeClass == null) {
        return acc;
      }
      const classificationInput = getMcbimItemClassificationInput({
        mcbimItem: mcBimItem,
        compCodePath,
      });
      if (
        deviceType === DeviceCategoryEnum.SUPPLY_AIR_DEVICE &&
        isSupplyAirTerminal(classificationInput)
      ) {
        device.direction = getVectorDirectionForAirTerminalByClosesDuctSegment(
          mcBimItem,
          mcBimItems
        );
        acc.push(device);
        return acc;
      }
      if (
        deviceType === DeviceCategoryEnum.RETURN_AIR_DEVICE &&
        isReturnAirterminal(classificationInput)
      ) {
        acc.push(device);
        return acc;
      }
      if (
        deviceType === DeviceCategoryEnum.LIGHT_DEVICE &&
        isKvadradiskInfeldArmatur(classificationInput)
      ) {
        acc.push(device);
        return acc;
      }
      if (
        deviceType === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE &&
        isFireSuppression(classificationInput)
      ) {
        acc.push(device);
        return acc;
      }
      if (deviceType === DeviceCategoryEnum.SENSOR_DEVICE && isSensor(classificationInput)) {
        acc.push(device);
        return acc;
      }
    }
    return acc;
  }, [] as IDeviceInputMl[]);
};

export const getVectorDirectionForAirTerminalByClosesDuctSegment = (
  mcBimItem: IMcBimItem,
  mcBimItems: IMcBimItem[]
): IDirectionDepricated | undefined => {
  const centerPointOfAirTerminal = mcBimItem.centerPoint as number[];
  if (centerPointOfAirTerminal == null) {
    return undefined;
  }

  const ductSegments = mcBimItems.filter(
    (mcBimItem) =>
      mcBimItem.type?.typeClass === "IfcDuctSegmentType" && mcBimItem.centerPoint != null
  );
  const closestDuctSegment = getMcBimItemClosesToCenterPoint(
    centerPointOfAirTerminal,
    ductSegments
  );
  if (closestDuctSegment?.centerPoint == null) {
    return undefined;
  }
  return [
    closestDuctSegment.centerPoint[0] - centerPointOfAirTerminal[0],
    closestDuctSegment.centerPoint[1] - centerPointOfAirTerminal[1],
  ];
};

export interface IGenericBimItemPolygon {
  bbox: IBoundingBox;
  deviceType: DeviceCategoryEnum;
  text: string;
  tooltip: string;
}

export const formatPixPolygonFromGenericBimItem = (args: IGenericBimItemPolygon): IPixPolygon => {
  return {
    id: generateRandomString(5),
    points: args.bbox,
    fillColor: DEVICES_TYPES_SETTINGS[args.deviceType]?.color || "#000000",
    strokeColor: DEVICES_TYPES_SETTINGS[args.deviceType]?.color || "#000000",
    strokeWidth: "0.05",
    text: {
      show: true,
      values: [args.text],
      color: "#000000",
      fontSize: "1",
    },
    tooltip: args.tooltip,
    isMoveable: false,
    isDragSelectable: false,
  };
};

export const createPixPathGridLinesFromMlResult = (
  results: IMLResult[] | undefined
): IPixPath[] => {
  if (results == null) {
    return [];
  }
  return (results || []).flatMap((result) => {
    if (result.grid != null) {
      return getPixPathForCeilingGrid(result.grid);
    } else {
      return [];
    }
  }) as IPixPath[];
};

export const getPixPathForCeilingGrid = (grid: IGrid): IPixPath[] => {
  return grid.map((line) => {
    return {
      id: generateRandomString(5),
      points: line,
      strokeColor: "#ccc",
      strokeWidth: "1",
    };
  });
};

export const generateRandomString = (chars: number) => {
  return Math.random()
    .toString(36)
    .substring(2, chars + 2);
};

// Typa ett enskilt objekt frÃ¥n GetMcbimSpacesApiResponse och lÃ¤gg till searchHit och fillColor
export type PartialMcBimSpace = (GetMcbimSpacesApiResponse extends Array<infer T> ? T : never) & {
  searchHit?: boolean;
  fillColor?: string;
};


export const getDirectionFromMlResultItem = (mlResult: IMLResult): IDirection => {
  if (mlResult.direction != null) {
    return mlResult.direction;
  }
  if (mlResult.bounding_box?.length !== 5) {
    return [0, 0];
  }
  return calculatePerpendicularVector(mlResult.bounding_box as IBoundingBox);
};

const calculatePerpendicularVector = (bbox: IBoundingBox): [number, number] => {
  if (bbox.length !== 5) {
    throw new Error("Bounding box must have exactly 5 points");
  }

  // Select the first side of the bounding box
  const [x1, y1] = bbox[0];
  const [x2, y2] = bbox[1];

  // Calculate the vector for the first side
  const vector: [number, number] = [x2 - x1, y2 - y1];

  // Calculate a perpendicular vector
  return [-vector[1], vector[0]];
};

export const getMcBimInstanceFromMlResult = (
  mlResult: IMLResult,
  mcbimItemsInSpace: IMcBimItem[] | undefined,
  compCodePath: string
): IMcBimItem | undefined => {
  if (mlResult.device_type === DeviceCategoryEnum.SUPPLY_AIR_DEVICE) {
    return mcbimItemsInSpace?.find((mcbimItem) =>
      isSupplyAirTerminal(getMcbimItemClassificationInput({ mcbimItem, compCodePath }))
    );
  }

  if (mlResult.device_type === DeviceCategoryEnum.LIGHT_DEVICE) {
    return mcbimItemsInSpace?.find((mcbimItem) =>
      isKvadradiskInfeldArmatur(getMcbimItemClassificationInput({ mcbimItem, compCodePath }))
    );
  }

  if (mlResult.device_type === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE) {
    return mcbimItemsInSpace?.find((mcbimItem) =>
      isFireSuppression(getMcbimItemClassificationInput({ mcbimItem, compCodePath }))
    );
  }

  if (mlResult.device_type === DeviceCategoryEnum.SENSOR_DEVICE) {
    return mcbimItemsInSpace?.find((mcbimItem) =>
      isSensor(getMcbimItemClassificationInput({ mcbimItem, compCodePath }))
    );
  }

  if (mlResult.device_type === DeviceCategoryEnum.RETURN_AIR_DEVICE) {
    return mcbimItemsInSpace?.find((mcbimItem) =>
      isReturnAirterminal(getMcbimItemClassificationInput({ mcbimItem, compCodePath }))
    );
  }

  return undefined;
};

export const getViewerPathBetweenAirTerminalAndConnectedFlowSegment = (
  mcbimItems: IMcBimItem[]
): IPixPath[] | undefined => {
  return mcbimItems.reduce((acc, mcBimItem) => {
    if (
      mcBimItem.instanceClass === "IfcAirTerminal" &&
      (mcBimItem.psets?.NONS_Reference?.RefCompType as string)?.startsWith("ST") === true &&
      mcBimItem.centerPoint != null
    ) {
      const centerPointOfAirTerminal = mcBimItem.centerPoint as [number, number];

      const ductsegments = mcbimItems.filter(
        (mcBimItem) =>
          mcBimItem.type?.typeClass === "IfcDuctSegmentType" && mcBimItem.centerPoint != null
      );
      const closesDuctSegmentToAirTerminal = getMcBimItemClosesToCenterPoint(
        centerPointOfAirTerminal,
        ductsegments
      );
      if (closesDuctSegmentToAirTerminal?.centerPoint != null) {
        acc.push({
          id: generateRandomString(5),
          points: [centerPointOfAirTerminal, closesDuctSegmentToAirTerminal.centerPoint],
          strokeColor: "red",
          strokeWidth: "1",
        });
      }
    }

    return acc;
  }, [] as IPixPath[]);
};

export const getMcBimItemClosesToCenterPoint = (
  centerPoint: number[],
  mcBimItems: IMcBimItem[]
): IMcBimItem | undefined => {
  return mcBimItems.reduce(
    (acc, mcBimItem) => {
      if (mcBimItem.centerPoint == null) return acc;
      const distance = Math.sqrt(
        Math.pow(centerPoint[0] - mcBimItem.centerPoint[0], 2) +
          Math.pow(centerPoint[1] - mcBimItem.centerPoint[1], 2)
      );
      if (acc == null || acc?.centerPoint == null) return mcBimItem;
      if (
        distance <
        Math.sqrt(
          Math.pow(centerPoint[0] - acc.centerPoint[0], 2) +
            Math.pow(centerPoint[1] - acc.centerPoint[1], 2)
        )
      ) {
        return mcBimItem;
      }
      return acc;
    },
    undefined as IMcBimItem | undefined
  );
};

export const removeMongoId = (obj: any) => {
  const { _id, ...rest } = obj;
  return rest;
};

export const getInputForMlData = (
  mcbimItemSpace: IMcBimItem,
  mcbimItemsInSpace: IMcBimItem[],
  compCodePath: string
): ISpaceTrainingDataInput | null => {
  return {
    _id: "1",
    projectNo: "10216575",
    space: {
      globalId: "1",
      polygon: mcbimItemSpace?.polygon as number[][],
      name: mcbimItemSpace?.name || "space",
      longName: mcbimItemSpace?.longName || "space",
    },
    supplyAirDevices: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.SUPPLY_AIR_DEVICE,
      compCodePath
    ), // formatMCBimItemToDevice(mcBimItems.map),
    returnAirDevices: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.RETURN_AIR_DEVICE,
      compCodePath
    ),
    fireSuppressionDevices: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE,
      compCodePath
    ),
    lightDevices: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.LIGHT_DEVICE,
      compCodePath
    ),
    furnitures: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.FURNITURE,
      compCodePath
    ),
    doors: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.DOOR,
      compCodePath
    ),
    lightCircleDevices: [],
    sensorDevices: formatMcBimItemsToDeviceInputML(
      mcbimItemsInSpace,
      DeviceCategoryEnum.SENSOR_DEVICE,
      compCodePath
    ),
  };
};

export const longNameExistsInMlInput = (
  name: string,
  mlInput: ISpaceTrainingDataInput | null
): boolean => {
  if (mlInput == null) {
    return false;
  }
  return (
    mlInput.supplyAirDevices.some((device) => device.name === name) ||
    mlInput.returnAirDevices.some((device) => device.name === name) ||
    mlInput.lightDevices.some((device) => device.name === name) ||
    mlInput.lightCircleDevices.some((device) => device.name === name) ||
    mlInput.fireSuppressionDevices.some((device) => device.name === name) ||
    mlInput.sensorDevices.some((device) => device.name === name) ||
    mlInput.furnitures.some((device) => device.name === name) ||
    mlInput.doors.some((device) => device.name === name)
  );
};

export const errorMessage = {
  title: "Feil",
  message: "Kunne ikke hente data",
  color: "red",
  icon: "",
};

export const getRandomNrBetween = (num1: number, num2: number): number => {
  return Math.floor(Math.random() * (num2 - num1 + 1) + num1);
};

// export const roomHasCeilingCoordinatesDepricated = (room: IRoom | null): boolean => {
//   try {
//     if (room == null) {
//       return false;
//     }
//     if (room.ceilingCoordinate == null) {
//       return false;
//     }
//     const xCoord = room.ceilingCoordinate[0];
//     const yCoord = room.ceilingCoordinate[1];
//     //Check if both are numbers
//     return typeof xCoord === "number" && typeof yCoord === "number";
//   } catch (err) {
//     return false;
//   }
// };

type IFileNameFloorJob = GetMcbimFileFloorVersionsApiResponse[number];

export const convertFileFLoorJobsToTreeNodes = (
  fileFloorJobs: IFileNameFloorJob[]
): TreeNodeData[] => {
  const fileTreeNodeData = fileFloorJobs.reduce((acc, fileFloorJob) => {
    const fileExist = acc.find((node) => node.label === fileFloorJob.fileName);
    if (fileExist == null) {
      acc.push({
        label: fileFloorJob.fileName,
        value: fileFloorJob.fileName,
        children: getChildrenForFile(fileFloorJob.fileName, fileFloorJobs),
      });
    }
    return acc;
  }, [] as TreeNodeData[]);
  return sortArrayByStrings(fileTreeNodeData, ["label"]);
};

const getChildrenForFile = (
  fileName: string,
  fileFloorJobs: IFileNameFloorJob[]
): TreeNodeData[] => {
  const _fileFloorJobs = fileFloorJobs.filter((fileFloorJob) => fileFloorJob.fileName === fileName);

  const arr = _fileFloorJobs.reduce((acc, fileFloorJob) => {
    const buldingStoreyExist = acc.find((node) => node.label === fileFloorJob.buildingStoryName);
    if (buldingStoreyExist == null) {
      acc.push({
        label: fileFloorJob.buildingStoryName,
        value: `${fileName}___${fileFloorJob.buildingStoryGlobalId}`,
        children: getChildrenForBuildingStorey(
          fileFloorJob.buildingStoryGlobalId,
          fileName,
          fileFloorJobs
        ),
      });
    }
    return acc;
  }, [] as TreeNodeData[]);
  return sortArrayByStrings(arr, ["label"]);
};

const getChildrenForBuildingStorey = (
  buildingStoreyGlobalId: string,
  fileName: string,
  fileFloorJobs: IFileNameFloorJob[]
) => {
  const _fileFloorJobs = fileFloorJobs.filter(
    (fileFloorJob) =>
      fileFloorJob.fileName === fileName &&
      fileFloorJob.buildingStoryGlobalId === buildingStoreyGlobalId
  );
  const arr = _fileFloorJobs.map((fileFloorJob) => {
    return {
      label: fileFloorJob.createdAt,
      value: `${fileName}___${buildingStoreyGlobalId}___${fileFloorJob.jobId}`,
    };
  });
  return sortArrayByStrings(arr, ["label"]).reverse();
};

export interface ICoordinate {
  x: number;
  y: number;
}

// Kontrollera om en punkt ligger inom ett polygon
const isPointInPolygon = (point: ICoordinate, polygon: number[][]): boolean => {
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0],
      yi = polygon[i][1];
    const xj = polygon[j][0],
      yj = polygon[j][1];
    const intersect =
      yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) isInside = !isInside;
  }
  return isInside;
};

export const getRandomCoordinateInPolygon = (polygon: number[][]): ICoordinate => {
  const minX = Math.min(...polygon.map((point) => point[0]));
  const minY = Math.min(...polygon.map((point) => point[1]));
  const maxX = Math.max(...polygon.map((point) => point[0]));
  const maxY = Math.max(...polygon.map((point) => point[1]));

  let randomPoint: ICoordinate;
  do {
    randomPoint = {
      x: getRandomNrBetween(minX, maxX),
      y: getRandomNrBetween(minY, maxY),
    };
  } while (!isPointInPolygon(randomPoint, polygon));

  return randomPoint;
};

interface IArg {
  ceilingCoordinate: number[];
  spacePolygon: IPolygon;
}

export const getCenterOfBoundingBox = (bbox: number[][]): [number, number] => {
  if (bbox?.length !== 5 && bbox?.length !== 4) {
    throw new Error("Bounding box must have exactly 5");
  }
  const x = (bbox[0][0] + bbox[2][0]) / 2;
  const y = (bbox[0][1] + bbox[2][1]) / 2;
  return [x, y];
};

export const getSqaureAroundCoordinate = (
  coordinate: number[],
  directionVector: IDirection,
  size: number = 0.6
): number[][] => {
  // Storleken pÃ¥ boundingboxen ska vara 0.6 * 0.6. Den ska vara centrerad runt coordinate roterad med riktningen i directionVector
  const halfSize = size / 2;
  const angle = Math.atan2(directionVector[1], directionVector[0]);
  const cosAngle = Math.cos(angle);
  const sinAngle = Math.sin(angle);

  const rotatePoint = (
    px: number,
    py: number,
    cx: number,
    cy: number,
    cosA: number,
    sinA: number
  ) => {
    const dx = px - cx;
    const dy = py - cy;
    return [cx + dx * cosA - dy * sinA, cy + dx * sinA + dy * cosA];
  };

  const x = coordinate[0];
  const y = coordinate[1];

  return [
    rotatePoint(x - halfSize, y - halfSize, x, y, cosAngle, sinAngle),
    rotatePoint(x + halfSize, y - halfSize, x, y, cosAngle, sinAngle),
    rotatePoint(x + halfSize, y + halfSize, x, y, cosAngle, sinAngle),
    rotatePoint(x - halfSize, y + halfSize, x, y, cosAngle, sinAngle),
    rotatePoint(x - halfSize, y - halfSize, x, y, cosAngle, sinAngle),
  ];
};

export const enum MCBimSpaceFillColors {
  WHITE = "#ffffff",
  RED = "#f8c4c4",
  GREEN = "#84f884",
  BLUE = "#8c8cff",
  YELLOW = "#e0e076",
  CYAN = "#00ffff",
  MAGENTA = "#ff00ff",
  LIGHT_GRAY = "#6B7C93",
  GREY = "#d3d3d3",
}

export const moveAndRotateBoundingBox = (
  boundingBox: IBoundingBox,
  coordinate: [number, number],
  direction: [number, number]
): IBoundingBox => {
  const angle = Math.atan2(direction[1], direction[0]);
  const cosAngle = Math.cos(angle);
  const sinAngle = Math.sin(angle);

  const rotatePoint = (
    px: number,
    py: number,
    cx: number,
    cy: number,
    cosA: number,
    sinA: number
  ): [number, number] => {
    const dx = px - cx;
    const dy = py - cy;
    return [cx + dx * cosA - dy * sinA, cy + dx * sinA + dy * cosA];
  };

  const x = coordinate[0];
  const y = coordinate[1];

  return [
    rotatePoint(boundingBox[0][0], boundingBox[0][1], x, y, cosAngle, sinAngle),
    rotatePoint(boundingBox[1][0], boundingBox[1][1], x, y, cosAngle, sinAngle),
    rotatePoint(boundingBox[2][0], boundingBox[2][1], x, y, cosAngle, sinAngle),
    rotatePoint(boundingBox[3][0], boundingBox[3][1], x, y, cosAngle, sinAngle),
    rotatePoint(boundingBox[0][0], boundingBox[0][1], x, y, cosAngle, sinAngle),
  ];
};

const getSideLengthSquared = (side: [[number, number], [number, number]]): number => {
  return (side[1][0] - side[0][0]) ** 2 + (side[1][1] - side[0][1]) ** 2;
};

const getLongestSide = (
  side0: [[number, number], [number, number]],
  side1: [[number, number], [number, number]]
) => {
  if (getSideLengthSquared(side0) > getSideLengthSquared(side1)) {
    return side0;
  }
  return side1;
};

const getDirectionOfSide = (side: [[number, number], [number, number]]): IDirectionDepricated => {
  const direction = [side[1][0] - side[0][0], side[1][1] - side[0][1]];
  const length = Math.sqrt(scalarProd(direction, direction));
  return direction.map((component) => component / length) as IDirectionDepricated;
};

const getCurrentOrientation = (boundingBox: [number, number][]): IDirectionDepricated => {
  const sides = boundingBox.map((point, index, points) => [
    point,
    points[(index + 1) % points.length],
  ]) as [[number, number], [number, number]][];
  const longestSide = sides.reduce(getLongestSide);
  return getDirectionOfSide(longestSide);
};

const rotateBetweenDirections = (
  points: [number, number][],
  startDirection: IDirectionDepricated,
  endDirection: IDirectionDepricated,
  rotationPoint: [number, number]
): [number, number][] => {
  const y0 = [-startDirection[1], startDirection[0]];
  const y1 = [-endDirection[1], endDirection[0]];
  const rotatedPoints = points
    .map((point) => vectorSubtract(point, rotationPoint))
    .map((point) => project(point, startDirection, y0))
    .map((point) => transform(point, endDirection, y1))
    .map((point) => vectorAdd(point, rotationPoint));
  return rotatedPoints;
};

interface moveBoundingBoxArgs {
  boundingBox: IBoundingBox;
  coordinate: [number, number];
  direction: IDirectionDepricated;
}
export const getRotatedBoundingBox = (args: moveBoundingBoxArgs): IBoundingBox => {
  const center = getCenterOfBoundingBox(args.boundingBox);
  const deltaX = args.coordinate[0] - center[0];
  const deltaY = args.coordinate[1] - center[1];
  const repositionedBox = args.boundingBox.map((point) => [
    point[0] + deltaX,
    point[1] + deltaY,
  ]) as [number, number][];
  const orientation = getCurrentOrientation(repositionedBox);
  const directionLength = Math.sqrt(scalarProd(args.direction, args.direction));
  const normalizedDirection = args.direction.map(
    (component) => component / directionLength
  ) as IDirectionDepricated;
  const rotatedBox = rotateBetweenDirections(
    repositionedBox,
    orientation,
    normalizedDirection,
    args.coordinate
  ) as IBoundingBox;
  return rotatedBox;
};


export const getApiBaseUrl = (): string => {
  const isInDev = process.env.NODE_ENV === "development";
  const baseUrl = isInDev ? "http://localhost:1337" : "https://multiplacer.multiconsult.no";
  return baseUrl;
};
